/**
 * DASHBOARD-RPS-TEAMS.WIDGET
 * Repartition of users for a survey
 */
import Card from "@/components/card"
import { useEffect, useState } from "react"
import { DashboardFilters, Filter, FilterLite } from "@/redux/filter.types"
import { store } from "@/index"
import { INSUFFICIENT_PARTICIPATION, STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { fetchGollacTeams } from "@/redux/_rps.actions"
import Dropdown from "@/components/dropdown"
import Space from "@/components/space"
import NoResultsWidget from "./no-results.widget"
import initFilters from "@/utils/init-filters.utils"
import { t } from "i18next"
import { useAppSelector } from "@/core/root-reducer"
import Plot from "react-plotly.js"
import PagePlaceholder from "@/components/page-placeholder"
import Link from "@/components/link"
import { filterReplaceDashboard } from "@/redux/filter.actions"

interface Props {
  filterName?: string
  isReadOnly?: boolean
  isReport?: boolean
}

export function DashboardRpsGollacTeamsWidget(props: Props) {
  // selectors
  const _session = useAppSelector((state) => state._session)
  const filter = useAppSelector((state) => state.filter)

  // states
  const [currentFilter, setCurrentFilter] = useState<Filter>(initCurrentFilter())
  const [status, setStatus] = useState(STATUS_LOADING)
  const [teams, setTeams] = useState<any>([])
  const [noData, setNoData] = useState(false)
  const [data, setData] = useState<Plotly.Data[]>([])

  useEffect(() => {
    setCurrentFilter(initCurrentFilter())
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterName])

  useEffect(() => {
    let searchFilter

    if (props.filterName) {
      searchFilter = filter.list.find((x) => x.name === props.filterName)
    } else if (_session.accountOptions.heatmapFilterName) {
      searchFilter = filter.list.find((x) => x.name === _session.accountOptions.heatmapFilterName)
    }

    setCurrentFilter(searchFilter || (filter.list.length ? filter.list[0] : new Filter()))
  }, [props.filterName, filter.list, _session.accountOptions])

  useEffect(() => {
    if (!currentFilter) return

    async function load() {
      setStatus(STATUS_LOADING)

      const response: any = await store.dispatch(
        fetchGollacTeams(currentFilter.name, filter.dashboard, undefined, undefined, props.isReport)
      )

      setStatus(STATUS_LOADED)

      const isEmpty =
        response.message === "no_data" ||
        (Array.isArray(response.teams_records) && response.teams_records.length === 0) ||
        (Array.isArray(response.teams) && response.teams.length === 0)

      if (isEmpty) {
        setNoData(true)
        setTeams([])
        setData([])
        return
      }

      if (!response.error) {
        const sorted = [...response.teams].sort((a, b) => b.exposed - a.exposed || b.exposed_pct - a.exposed_pct)

        setTeams(sorted)
        setData(buildPlotData(sorted))
      }
    }

    load()
  }, [currentFilter, filter.dashboard])

  function buildPlotData(sortedTeams: any[]): Plotly.Data[] {
    return [
      {
        x: sortedTeams.map((t) => t.exposed),
        y: sortedTeams.map((t) => t.attribute_name),
        text: sortedTeams.map((t) => t.exposed),
        name: t("employees_exposed"),
        orientation: "h",
        type: "bar",
        marker: { color: "#eb5a46" }
      },
      {
        x: sortedTeams.map((t) => t.safe),
        y: sortedTeams.map((t) => t.attribute_name),
        text: sortedTeams.map((t) => t.safe),
        name: t("employees_safe"),
        orientation: "h",
        type: "bar",
        marker: { color: "#20CA7E" }
      }
    ]
  }

  const layout: any = {
    margin: {
      l: 0,
      r: 0,
      b: 0,
      t: 0
    },
    showlegend: false,
    bargap: 0.1,
    barmode: "stack",
    xaxis: {
      showgrid: false,
      showline: false
    },
    yaxis: {
      margin: { l: 5 },
      showgrid: false,
      showline: false,
      autorange: "reversed"
    },
    width: 600
  }

  //Return default heatmap filter
  function initCurrentFilter() {
    let searchFilter: Filter | undefined = undefined
    if (props.filterName) {
      searchFilter = filter.list.find((x: Filter) => x.name === props.filterName)
    } else if (_session.accountOptions.heatmapFilterName) {
      searchFilter = filter.list.find((x: Filter) => x.name === _session.accountOptions.heatmapFilterName)
    }

    if (searchFilter) {
      return searchFilter
    } else {
      return filter.list.length ? filter.list[0] : new Filter()
    }
  }

  function clickTeams(team) {
    store.dispatch(
      filterReplaceDashboard(
        new DashboardFilters({
          customFilters: [new FilterLite({ id: currentFilter.id, attributesIds: String(team.attribute_id).split("_") })]
        })
      )
    )
  }

  return status === INSUFFICIENT_PARTICIPATION ? (
    <NoResultsWidget />
  ) : (
    <Card className="flex1" status={status}>
      {noData ? (
        <PagePlaceholder
          image="sherlock"
          title={t("rps_teams_no_data_title")}
          help={t("survey_no_results_help", { count: _session.participationMin })}
        />
      ) : (
        <>
          {!props.isReport && (
            <div className="flex">
              <div>
                <h2>{t("rps_teams_title")}</h2>
                <p className="grey-t text-sm">{t("rps_teams_participation_min", { min: _session.participationMin })}</p>
              </div>
              <Space />
              {props.isReadOnly ? (
                <p>{currentFilter.name}</p>
              ) : (
                <Dropdown
                  displayField="name"
                  list={initFilters(true, true)}
                  onSelect={(filter: Filter) => setCurrentFilter(filter)}
                  value={currentFilter.name}
                />
              )}
            </div>
          )}
        </>
      )}

      <div className="flex" style={{ marginTop: "12px" }}>
        <div>
          {teams.map((team: any) => (
            <div
              key={team.attribute_id}
              style={{
                margin: "6px 20px 12px 0px",
                whiteSpace: "nowrap"
              }}>
              <Link isWithoutMargin onClick={() => clickTeams(team)}>
                {team.attribute_name}
              </Link>
              {/* <div className="grey-t">{team.attribute_name}</div> */}
            </div>
          ))}
        </div>

        <Plot
          data={data}
          layout={layout}
          config={{
            displayModeBar: false
          }}
        />
        <div id="plotly" />
      </div>
    </Card>
  )
}
